import { newsList as rawNewsList } from '../mocks/news';
import type { NewsArticle, NewsArticleSource } from '../mocks/news';
import {
  fetchNewsByIdFromCms,
  fetchNewsCategoriesFromCms,
  fetchNewsListFromCms,
  isCmsNewsApiEnabled,
} from './cmsNewsApi';

export type Locale = 'zh-CN' | 'en' | 'zh-TW';
/** 与迅睿 dr_1_share_category.dirname 对齐 */
export type NewsCategorySlug = string;

interface LocalizedText {
  'zh-CN': string;
  en: string;
  'zh-TW': string;
}

interface LocalizedNewsArticle {
  id: string;
  category: NewsCategorySlug;
  date: string;
  readTime: LocalizedText;
  author: LocalizedText;
  authorRole: LocalizedText;
  title: LocalizedText;
  summary: LocalizedText;
  image: string;
  tags: LocalizedText[];
  content: LocalizedText;
}

interface NewsCategoryBucket {
  key: NewsCategorySlug;
  articles: LocalizedNewsArticle[];
}

export interface GetNewsListParams {
  /** 任意语言码（如 i18n），内部会规范为 Locale */
  locale?: string;
  category?: 'all' | NewsCategorySlug;
  page?: number;
  pageSize?: number;
}

export interface GetNewsListResponse {
  items: NewsArticle[];
  total: number;
  page: number;
  pageSize: number;
}

const MOCK_DELAY_MS = 250;

/** CMS 未上传封面时的占位图（与 mock 首条使用同一可解析的 readdy 资源，避免无 seq 的短链被 API 拒绝） */
const CMS_NEWS_PLACEHOLDER_IMAGE =
  'http://geel.fp.08wx.cn/images/88c920e85af153184e5a594d908de34d.jpg';

/** 与迅睿 dr_1_share_category（dirname / name / name_en / name_tw）对齐，API 接入前用于离线 mock */
const CATEGORY_LABELS: Record<string, Record<Locale, string>> = {
  product: {
    'zh-CN': '产品动态',
    en: 'Product Updates',
    'zh-TW': '產品動態',
  },
  industry: {
    'zh-CN': '行业资讯',
    en: 'Industry News',
    'zh-TW': '行業資訊',
  },
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** 栏目角标配色：与 dirname 绑定，未知栏目默认 product 样式 */
export function categoryBadgeClass(slug: string): string {
  return slug === 'industry'
    ? 'text-accent border-accent/40 bg-accent/10'
    : 'text-primary border-primary/40 bg-primary/10';
}

function resolveLocale(input?: string): Locale {
  const lang = (input ?? 'zh-CN').toLowerCase();
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('zh-tw') || lang.startsWith('zh-hk')) return 'zh-TW';
  return 'zh-CN';
}

function toLocalizedText(zhCN: string): LocalizedText {
  return {
    'zh-CN': zhCN,
    en: zhCN,
    'zh-TW': zhCN,
  };
}

function normalizeTraditional(text: string): string {
  const replaceMap: Array<[string, string]> = [
    ['发布', '發布'],
    ['平台', '平臺'],
    ['数据', '數據'],
    ['用户', '用戶'],
    ['网络', '網絡'],
    ['系统', '系統'],
    ['性能', '效能'],
    ['资产', '資產'],
    ['链', '鏈'],
  ];

  let output = text;
  for (const [from, to] of replaceMap) {
    output = output.split(from).join(to);
  }

  return output;
}

function enrichLocales(article: NewsArticleSource): LocalizedNewsArticle {
  return {
    id: article.id,
    category: article.category,
    date: article.date,
    readTime: {
      'zh-CN': article.readTime,
      en: article.readTime.replace('分钟', 'min read'),
      'zh-TW': normalizeTraditional(article.readTime.replace('分钟', '分鐘')),
    },
    author: {
      'zh-CN': article.author,
      en: article.author,
      'zh-TW': normalizeTraditional(article.author),
    },
    authorRole: {
      'zh-CN': article.authorRole,
      en: article.authorRole,
      'zh-TW': normalizeTraditional(article.authorRole),
    },
    title: {
      'zh-CN': article.title,
      en: article.title,
      'zh-TW': normalizeTraditional(article.title),
    },
    summary: {
      'zh-CN': article.summary,
      en: article.summary,
      'zh-TW': normalizeTraditional(article.summary),
    },
    image: article.image,
    tags: article.tags.map((tag) => ({
      'zh-CN': tag,
      en: tag,
      'zh-TW': normalizeTraditional(tag),
    })),
    content: {
      'zh-CN': article.content,
      en: article.content,
      'zh-TW': normalizeTraditional(article.content),
    },
  };
}

const localizedPool: LocalizedNewsArticle[] = rawNewsList.map(enrichLocales);

const categoryBuckets: NewsCategoryBucket[] = [
  {
    key: 'product',
    articles: localizedPool.filter((item) => item.category === 'product'),
  },
  {
    key: 'industry',
    articles: localizedPool.filter((item) => item.category === 'industry'),
  },
];

function toNewsArticle(article: LocalizedNewsArticle, locale: Locale): NewsArticle {
  const slug = article.category;
  const categoryLabel = CATEGORY_LABELS[slug]?.[locale] ?? slug;

  return {
    id: article.id,
    category: slug,
    categoryLabel,
    date: article.date,
    readTime: article.readTime[locale],
    author: article.author[locale],
    authorRole: article.authorRole[locale],
    title: article.title[locale],
    summary: article.summary[locale],
    image: article.image,
    tags: article.tags.map((tag) => tag[locale]),
    content: article.content[locale],
  };
}

function getPoolByCategory(category: 'all' | NewsCategorySlug): LocalizedNewsArticle[] {
  if (category === 'all') {
    return categoryBuckets.flatMap((bucket) => bucket.articles);
  }
  const found = categoryBuckets.find((bucket) => bucket.key === category);
  return found ? found.articles : [];
}

function ensureNewsImage(article: NewsArticle): NewsArticle {
  if (article.image?.trim()) return article;
  return { ...article, image: CMS_NEWS_PLACEHOLDER_IMAGE };
}

/** CMS 已由接口按 locale 返回标题/正文等，这里只统一栏目角标文案与阅读时长展示 */
function overlayCmsNewsPresentation(article: NewsArticle, locale: Locale): NewsArticle {
  const slug = article.category;
  const categoryLabel = CATEGORY_LABELS[slug]?.[locale] ?? article.categoryLabel;
  const readTime =
    locale === 'en'
      ? article.readTime.replace('分钟', 'min read')
      : locale === 'zh-TW'
        ? article.readTime.replace('分钟', '分鐘')
        : article.readTime;

  return {
    ...article,
    categoryLabel,
    readTime,
  };
}

export async function getNewsList(params: GetNewsListParams = {}): Promise<GetNewsListResponse> {
  const locale = resolveLocale(params.locale);

  if (isCmsNewsApiEnabled()) {
    const result = await fetchNewsListFromCms({
      locale,
      category: params.category ?? 'all',
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 500,
    });
    return {
      ...result,
      items: result.items.map((item) => overlayCmsNewsPresentation(ensureNewsImage(item), locale)),
    };
  }

  await sleep(MOCK_DELAY_MS);

  const category = params.category ?? 'all';
  const page = params.page ?? 1;
  const source = getPoolByCategory(category);
  const pageSize = params.pageSize ?? source.length;

  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    items: source.slice(start, end).map((item) => toNewsArticle(item, locale)),
    total: source.length,
    page,
    pageSize,
  };
}

export async function getNewsById(id: string, localeArg?: string): Promise<NewsArticle | null> {
  const locale = resolveLocale(localeArg);

  if (isCmsNewsApiEnabled()) {
    const article = await fetchNewsByIdFromCms(id, locale);
    return article ? overlayCmsNewsPresentation(ensureNewsImage(article), locale) : null;
  }

  await sleep(MOCK_DELAY_MS);
  const matched = localizedPool.find((item) => item.id === id);
  return matched ? toNewsArticle(matched, locale) : null;
}

export async function getNewsCategories(
  localeArg?: string,
): Promise<Array<{ key: NewsCategorySlug; label: string; total: number }>> {
  const locale = resolveLocale(localeArg);

  if (isCmsNewsApiEnabled()) {
    const cms = await fetchNewsCategoriesFromCms(locale);
    return cms.map((c) => ({
      key: c.key,
      label: c.label,
      total: c.total,
    }));
  }

  await sleep(MOCK_DELAY_MS);

  return categoryBuckets.map((bucket) => ({
    key: bucket.key,
    label: CATEGORY_LABELS[bucket.key]?.[locale] ?? bucket.key,
    total: bucket.articles.length,
  }));
}
